import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { get } from 'lodash';
import { environment } from 'src/environments/environment';
import { ICommentPayload, IRequestComment } from '../models/comment';
import { CreateCommentResult, FilterUsersResult, PostAssetCommentPagedResult, ShootAssetCommentPagedResult } from '../models/comment-result';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class CommentService {
  private avatar$ = new BehaviorSubject<any>({});
  public avatarData = this.avatar$.asObservable();

  connectionId = '';
  httpOptionsSignalR = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }),
  };

  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer,
  ) {}

  setConnectionId(connectionId: string) {
    this.connectionId = connectionId;
    this.httpOptionsSignalR = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'x-connection-id': this.connectionId,
      }),
    };
  }

  getShootAssetComment(
    productTouchAssetId: string,
    payload: IRequestComment,
  ): Observable<ShootAssetCommentPagedResult> {
    let url = `${environment.apiBase}/productTouchAssets/${productTouchAssetId}/shootAssets/comments`;
    url += `?pageNumber=${payload.pageNumber}&pageSize=${payload.pageSize}`;
    if (isDevMode()) {
      console.log('[DEV] [CommentService] loading shoot asset comments', productTouchAssetId);
    }
    return this.http.get<ShootAssetCommentPagedResult>(url, httpOptions);
  }
  sentShootAssetComment(
    productTouchAssetId: string,
    comment: ICommentPayload,
  ): Observable<CreateCommentResult> {
    const url = `${environment.apiBase}/productTouchAssets/${productTouchAssetId}/shootAssets/comments`;
    if (isDevMode()) {
      console.log('[DEV] [CommentService] sending shoot asset comment', productTouchAssetId);
    }
    return this.http.post<CreateCommentResult>(url, comment, this.httpOptionsSignalR);
  }

  getPostAssetComment(
    productTouchAssetId: string,
    payload: IRequestComment,
    isInternal: boolean,
    masterSuiteTouchAssetId?: string,
  ): Observable<PostAssetCommentPagedResult> {
    let url = `${environment.apiBase}/productTouchAssets/${productTouchAssetId}/postAssets/${masterSuiteTouchAssetId}/comments`;
    if (isInternal) {
      url += '/internal';
    }
    url += `?pageNumber=${payload.pageNumber}&pageSize=${payload.pageSize}`;
    if (isDevMode()) {
      console.log('[DEV] [CommentService] loading post asset comments', productTouchAssetId);
    }
    return this.http.get<PostAssetCommentPagedResult>(url, httpOptions);
  }
  sentPostAssetComment(
    productTouchAssetId: string,
    comment: ICommentPayload,
    isInternal: boolean,
  ): Observable<CreateCommentResult> {
    let url = `${environment.apiBase}/productTouchAssets/${productTouchAssetId}/postAssets/comments`;
    if (isInternal) {
      url += '/internal';
    }
    if (isDevMode()) {
      console.log('[DEV] [CommentService] sending post asset comment', productTouchAssetId);
    }
    return this.http.post<CreateCommentResult>(url, comment, this.httpOptionsSignalR);
  }

  filterUserByKey(key: string, organizationId?: string): Observable<FilterUsersResult> {
    let url = `${environment.apiBase}/users/filter`;
    url += `?search=${key}`;
    if (organizationId) {
      url += `&organizationId=${organizationId}`;
    }
    return this.http.get<FilterUsersResult>(url, httpOptions);
  }

  mappingAvatar(id: string) {
    return get(this.avatar$.value, id, '');
  }

  setAvatar(url: string, data: any) {
    const avatarData = {
      ...this.avatar$.value,
      [url]: data,
    };
    this.avatar$.next(avatarData);
    sessionStorage.setItem('avatar', JSON.stringify(avatarData));
  }

  getAvatar(url: string) {
    return this.http
      .get(url, { responseType: 'blob' })
      .pipe(map((val: any) => this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(val))));
  }
}
